// src/components/Consultant/ConsultantUtils.js
import React from "react";
import { Paper, Typography, Tooltip } from "@mui/material";
import { trackedUrl, hasValidEmail as _hasValidEmail, hasValidPhone as _hasValidPhone } from "../../utils/trackedLinks";

/* -----------------------------
   Timestamp
------------------------------ */
export const formatTimestamp = (timestamp) => {
  if (!timestamp) return "N/A";
  const date = timestamp?.toDate
    ? timestamp.toDate()
    : timestamp instanceof Date
    ? timestamp
    : new Date(timestamp);

  if (Number.isNaN(date.getTime())) return "Invalid Date";

  return (
    date.toLocaleDateString() +
    " " +
    date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  );
};

/* -----------------------------
   Validation
   - Use the shared utils so behavior matches the rest of the app
------------------------------ */
export const hasValidEmail = (e) => _hasValidEmail(e);
export const hasValidPhone = (p) => _hasValidPhone(p);

/* -----------------------------
   Tracking Links (FIXED)
   - Old version returned mailto/tel/wa.me (no backend tracking) and often '#'
   - New version builds the backend tracked redirect:
     /api/t?channel=...&to=...&source=...
   - Supports: tel | whatsapp | email | gmail | outlook | yahoo | calendar
------------------------------ */
export const getTrackedLink = (channel, to, meta = {}) => {
  const source = meta.source || "consultant";

  return (
    trackedUrl(channel, {
      to,
      source,
      contact_id: meta.contact_id,
      contact_name: meta.contact_name,
      subject: meta.subject,
      body: meta.body,
      text: meta.text,
      target: meta.target,
    }) || "#"
  );
};

/* -----------------------------
   Priority Logic (kept)
------------------------------ */
export const getTravelPriority = (travelDateRaw) => {
  if (!travelDateRaw) return { level: "normal", label: "", days: null };

  const today = new Date();
  const travelDate = new Date(travelDateRaw);

  today.setHours(0, 0, 0, 0);
  travelDate.setHours(0, 0, 0, 0);

  const diffTime = travelDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      level: "overdue",
      label: `Overdue (${Math.abs(diffDays)}d)`,
      days: diffDays,
      color: "#d32f2f",
    };
  }
  if (diffDays <= 7) {
    const text =
      diffDays === 0 ? "Today" : diffDays === 1 ? "Tomorrow" : `In ${diffDays} days`;
    return { level: "urgent", label: `⚡ ${text}`, days: diffDays, color: "#f44336" };
  }
  if (diffDays <= 30) {
    return { level: "hot", label: `🔥 In ${diffDays} days`, days: diffDays, color: "#ff9800" };
  }
  return { level: "normal", label: "Pipeline", days: diffDays, color: "transparent" };
};

/* -----------------------------
   UI helper (kept)
------------------------------ */
export const CustomerDetailItem = ({ label, value, isHighlight }) => (
  <Paper
    variant="outlined"
    sx={{
      p: 1,
      borderRadius: 2,
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
      minWidth: 0,
      bgcolor: isHighlight ? "rgba(45, 184, 56, 0.04)" : "background.paper",
      borderColor: isHighlight ? "primary.main" : "divider",
      transition: "all 0.2s ease",
      "&:hover": {
        borderColor: "primary.main",
        transform: "translateY(-1px)",
        boxShadow: 1,
      },
    }}
  >
    <Typography
      variant="caption"
      noWrap
      sx={{
        color: "text.secondary",
        fontWeight: 700,
        textTransform: "uppercase",
        fontSize: "0.65rem",
        letterSpacing: "0.5px",
        mb: 0.25,
      }}
    >
      {label}
    </Typography>

    <Tooltip title={value == null ? "" : String(value)}>
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontWeight: isHighlight ? 700 : 500,
          color: isHighlight ? "primary.dark" : "text.primary",
          fontSize: "0.85rem",
        }}
      >
        {value || "—"}
      </Typography>
    </Tooltip>
  </Paper>
);
